import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Chess } from 'chess.js';
import { api } from '@/lib/api';
import ChessBoardView from '@/components/chess/ChessBoardView';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Trophy, MessageCircle, ListOrdered, Users, ChevronLeft, ChevronRight, SkipBack, SkipForward, Crown } from 'lucide-react';

export default function RecapView() {
  const { token } = useParams();
  const [r, setR] = useState(null);
  const [err, setErr] = useState(null);
  const [ply, setPly] = useState(0);
  useEffect(() => {
    api.get(`/classroom-recaps/shared/${token}`).then(x => { setR(x.data); setPly((x.data.moves||[]).length); })
      .catch(e => setErr(e.response?.data?.detail || 'Enlace inválido'));
  }, [token]);
  // Precompute the full authoritative sequence of FENs once (each ply is the
  // guaranteed starting position of the next move, never recomputed on navigation).
  const positions = useMemo(() => {
    if (!r) return [];
    const c = new Chess(r.starting_fen || undefined);
    const fens = [c.fen()];
    for (const m of (r.moves || [])) {
      if (m?.fen) { fens.push(m.fen); try { c.load(m.fen); } catch {} continue; }
      try { c.move(m.san); fens.push(c.fen()); } catch { break; }
    }
    return fens;
  }, [r]);
  const fen = useMemo(() => {
    if (!positions.length) return null;
    const idx = Math.min(Math.max(0, ply), positions.length - 1);
    return positions[idx];
  }, [positions, ply]);
  if (err) return <div className="min-h-screen grid place-items-center"><div className="rounded-2xl bg-white border p-8 text-center max-w-md"><div className="font-display text-2xl font-bold">Enlace no válido</div><div className="text-slate-500 mt-2">{err}</div></div></div>;
  if (!r) return <div className="p-12 text-slate-500">Cargando…</div>;
  return (
    <div className="min-h-screen bg-[#F1F5F9] p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-xl bg-slate-900 grid place-items-center"><Crown className="w-4 h-4 text-amber-300"/></div>
          <div>
            <div className="font-display font-extrabold text-lg">Chess4Kings · Recap</div>
            <div className="text-xs text-slate-500 uppercase tracking-widest font-bold">Sesión guardada · sólo lectura</div>
          </div>
        </div>
        <div className="mb-4">
          <div className="font-display text-3xl font-extrabold">{r.name}</div>
          <div className="text-sm text-slate-500 font-mono">
            {new Date(r.started_at).toLocaleString()} · {r.participants?.length || 0} participantes
          </div>
        </div>
        <div className="grid lg:grid-cols-[1fr_420px] gap-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-col items-center">
            <ChessBoardView fen={fen} boardWidth={560} arePiecesDraggable={false}/>
            <div className="mt-3 flex items-center gap-2">
              <Button data-testid="recap-first" size="icon" variant="outline" onClick={()=>setPly(0)}><SkipBack className="w-4 h-4"/></Button>
              <Button data-testid="recap-prev" size="icon" variant="outline" onClick={()=>setPly(p=>Math.max(0,p-1))}><ChevronLeft className="w-4 h-4"/></Button>
              <Button data-testid="recap-next" size="icon" variant="outline" onClick={()=>setPly(p=>Math.min((r.moves||[]).length, p+1))}><ChevronRight className="w-4 h-4"/></Button>
              <Button data-testid="recap-last" size="icon" variant="outline" onClick={()=>setPly((r.moves||[]).length)}><SkipForward className="w-4 h-4"/></Button>
              <div className="ml-2 text-xs font-mono text-slate-500">jugada {ply}/{(r.moves||[]).length}</div>
            </div>
          </div>
          <aside className="bg-white rounded-2xl border border-slate-200 flex flex-col min-h-0">
            <Tabs defaultValue="leaderboard" className="flex-1 flex flex-col min-h-0">
              <TabsList className="justify-start rounded-none border-b bg-white p-1 mx-2 mt-2">
                <TabsTrigger value="leaderboard"><Trophy className="w-4 h-4 mr-1"/> Ranking</TabsTrigger>
                <TabsTrigger value="moves"><ListOrdered className="w-4 h-4 mr-1"/> Jugadas</TabsTrigger>
                <TabsTrigger value="chat"><MessageCircle className="w-4 h-4 mr-1"/> Chat</TabsTrigger>
                <TabsTrigger value="participants"><Users className="w-4 h-4 mr-1"/> Alumnos</TabsTrigger>
              </TabsList>
              <TabsContent value="leaderboard" className="p-4 overflow-y-auto max-h-[520px]">
                {(r.leaderboard||[]).map((row,i) => (
                  <div key={row.user_id} className="flex items-center justify-between p-2 border-b">
                    <div className="flex items-center gap-2">
                      <div className={`w-7 h-7 rounded-full grid place-items-center text-white font-bold text-xs ${i===0?'bg-amber-500':i===1?'bg-slate-400':i===2?'bg-orange-500':'bg-slate-300'}`}>{i+1}</div>
                      <div className="font-semibold text-sm">{row.name}</div>
                    </div>
                    <div className="font-display font-bold">{row.score}</div>
                  </div>
                ))}
                {(!r.leaderboard || r.leaderboard.length === 0) && <div className="text-sm text-slate-500">Sin ranking.</div>}
              </TabsContent>
              <TabsContent value="moves" className="p-4 overflow-y-auto max-h-[520px] font-mono text-sm">
                {(r.moves||[]).map((m,i) => (
                  <span key={i} onClick={()=>setPly(i+1)} className={`inline-block cursor-pointer px-1 rounded ${ply===i+1?'bg-amber-200':'hover:bg-slate-100'}`}>
                    {i%2===0 && <span className="text-slate-400">{Math.floor(i/2)+1}.</span>} {m.san}
                  </span>
                ))}
              </TabsContent>
              <TabsContent value="chat" className="p-4 overflow-y-auto max-h-[520px] space-y-2">
                {(r.chat||[]).map(m => (
                  <div key={m.id}>
                    <div className="text-xs text-slate-500 font-semibold">{m.name}</div>
                    <div className="inline-block bg-slate-100 rounded-2xl px-3 py-1.5 text-sm">{m.text}</div>
                  </div>
                ))}
              </TabsContent>
              <TabsContent value="participants" className="p-4 overflow-y-auto max-h-[520px]">
                {(r.participants||[]).map(p => (
                  <div key={p.user_id} className="py-2 border-b text-sm">
                    <div className="font-semibold">{p.name}</div>
                    <div className="text-xs text-slate-500 capitalize">{p.role?.replace('_',' ')}</div>
                  </div>
                ))}
              </TabsContent>
            </Tabs>
          </aside>
        </div>
        <div className="text-center text-xs text-slate-400 mt-8 font-mono">Recap generado el {new Date(r.ended_at).toLocaleString()}</div>
      </div>
    </div>
  );
}
